import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createMetadataSchema } from "./schema";
import { createDefaultValues } from "./utils";
import type { ProjectMetadataField } from "@/types/api";

interface UseMetadataFormProps {
  isOpen: boolean;
  recordId: string | null;
  metadataFields: ProjectMetadataField[];
  initialValues?: Record<string, unknown>;
  recordDefaults: {
    tags?: string[];
    planId?: string | null;
    pinX?: number | null;
    pinY?: number | null;
    pinBearing?: number | null;
  };
}

export function useMetadataForm({
  isOpen,
  recordId,
  metadataFields,
  initialValues,
  recordDefaults,
}: UseMetadataFormProps) {
  const schema = createMetadataSchema(metadataFields);
  const defaultValues = createDefaultValues(
    metadataFields,
    initialValues,
    recordDefaults,
  );

  const form = useForm<Record<string, unknown>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    defaultValues,
    mode: "onSubmit",
  });

  // Reset only when the record identity changes — not on every isOpen toggle.
  // For new records (recordId null), the modal may close/reopen during markup
  // re-edit; we must preserve the user's in-progress form state across that.
  useEffect(() => {
    if (isOpen) {
      form.reset(
        createDefaultValues(metadataFields, initialValues, recordDefaults),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);

  return form;
}
